<template>
  <div class="fu-drh" data-test="fu-drawer-header">
    <span class="fu-drh__avatar" aria-hidden="true">{{ avatar }}</span>

    <div class="fu-drh__main">
      <div class="fu-drh__eyebrow" data-test="fu-drawer-title">{{ title }}</div>
      <div class="fu-drh__name-row">
        <button
          type="button"
          class="fu-drh__name"
          title="Open patient profile"
          data-test="fu-drawer-name"
          @click="$emit('open-profile')"
        >{{ name }}</button>
        <span v-if="demographics" class="fu-drh__demo" data-test="fu-drawer-demographics">{{ demographics }}</span>
        <span
          v-if="premed"
          class="fu-drh__premed"
          title="Premedication required before treatment — flagged in Open Dental."
          data-test="fu-drawer-premed"
        ><fu-icon name="alert" :size="13" />Premed</span>
      </div>

      <div v-if="contacts.length || address" class="fu-drh__contacts">
        <span
          v-for="c in contacts"
          :key="c.kind"
          class="fu-drh__pill"
          :data-test="`fu-drawer-contact-${c.kind}`"
        ><fu-icon :name="iconOf(c.kind)" :size="14" :label="labelOf(c.kind)" />{{ c.value }}</span>
        <span v-if="address" class="fu-drh__address" data-test="fu-drawer-address"><fu-icon name="pin" :size="14" label="Address" />{{ address }}</span>
      </div>
    </div>

    <button type="button" class="fu-drh__close" aria-label="Close" data-test="fu-drawer-close" @click="$emit('close')">
      <fu-icon name="x" :size="18" />
    </button>
  </div>
</template>

<script>
/**
 * DrawerHeader — avatar, queue title, the patient's name (a button to the profile), age / sex, the
 * premed badge, contact pills and the address (the design's drawer header; today's
 * CreateFollowup.vue:17–76 data).
 *
 *   title         the queue's title ("Overdue Recall Follow-up")
 *   name          the display name, ALREADY masked at the data boundary (never masked here)
 *   initials      optional; else derived from `name` (drawerFacts.initialsOf)
 *   demographics  "46 yrs · F" — the container formats it (formatAge / getGender live in the mixin)
 *   premed        drawerFacts.hasPremed(patient)
 *   contacts      drawerFacts.contactsOf(patient) → [{ kind: home|mobile|work|email, value }]
 *   address       drawerFacts.addressOf(patient)
 * Events: open-profile, close. Presentational: no store, no mixin, no $route.
 */
import FuIcon from '../FuIcon.vue';
import { initialsOf } from '@/utils/followups/drawer/drawerFacts.js';

const ICON = { home: 'home', mobile: 'mobile', work: 'brief', email: 'mail' };
const LABEL = { home: 'Home phone', mobile: 'Mobile phone', work: 'Work phone', email: 'Email' };

export default {
  name: 'DrawerHeader',
  components: { FuIcon },
  props: {
    title: { type: String, default: '' },
    name: { type: String, default: '' },
    initials: { type: String, default: '' },
    demographics: { type: String, default: '' },
    premed: { type: Boolean, default: false },
    contacts: { type: Array, default: () => [] },
    address: { type: String, default: '' },
  },
  computed: {
    avatar() {
      return this.initials || initialsOf(this.name);
    },
  },
  methods: {
    iconOf(kind) { return ICON[kind] || 'info'; },
    labelOf(kind) { return LABEL[kind] || kind; },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drh {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
}

.fu-drh__avatar {
  flex: none;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $fu-brand-100;
  color: $fu-brand-700;
  font-weight: 800;
  font-size: 18px;
}

.fu-drh__main { flex: 1; min-width: 0; }

.fu-drh__eyebrow {
  font-size: 10.5px;
  font-weight: $fu-fw-bold;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $fu-brand-600;
}

.fu-drh__name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 3px;
}

.fu-drh__name {
  @include fu-button-reset;
  @include fu-focus-ring;
  @include fu-font-display;
  border-radius: $fu-radius-xs;
  font-weight: 800;
  font-size: 24px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: $fu-text;
  overflow-wrap: anywhere;
  &:hover { color: $fu-brand-700; }
}

.fu-drh__demo {
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
  white-space: nowrap;
}

.fu-drh__premed {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 9px;
  border-radius: 20px;
  background: $fu-danger-subtle;
  color: $fu-danger-text;
  font-size: 11.5px;
  font-weight: $fu-fw-bold;
}

.fu-drh__contacts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}

.fu-drh__pill {
  @include fu-tabular;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 28px;
  padding: 0 11px;
  border-radius: 20px;
  background: $fu-neutral-50;
  border: 1px solid $fu-border;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  .fu-icon { flex: none; color: $fu-text-subtle; }
}

.fu-drh__address {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 0 4px;
  font-size: 12.5px;
  color: $fu-text-muted;
  .fu-icon { flex: none; color: $fu-text-subtle; }
}

.fu-drh__close {
  @include fu-icon-btn(32px);
}

// Tablet (< 900px drawer): the cards stack, so every px of header is a px less form — a smaller
// avatar and name and tighter pills (the header + facts took ~245px at 820).
.fu-dr--tablet .fu-drh { gap: 12px; }
.fu-dr--tablet .fu-drh__avatar { width: 42px; height: 42px; font-size: 15px; }
.fu-dr--tablet .fu-drh__name { font-size: 20px; }
.fu-dr--tablet .fu-drh__name-row { margin-top: 1px; }
.fu-dr--tablet .fu-drh__contacts { margin-top: 6px; gap: 4px 6px; }
.fu-dr--tablet .fu-drh__pill { height: 24px; padding: 0 9px; font-size: 12px; }
.fu-dr--tablet .fu-drh__address { font-size: 12px; }

.fu-dr--phone .fu-drh__name { font-size: 20px; }
.fu-dr--phone .fu-drh { gap: 12px; }
.fu-dr--phone .fu-drh__avatar { width: 44px; height: 44px; font-size: 15px; }
</style>
